/**
 * Expense Workflow Service
 *
 * Handles business logic and workflows for expenses.
 * This service manages validations, receipt handling, and other business operations.
 */

import { RepositoryResult } from './BaseRepository';
import { Expense } from '@/shared/types';

/**
 * Expense Workflow Service
 * Provides business logic and workflow operations for expenses
 */
export class ExpenseWorkflowService {
  /**
   * Validate expense data before creation/update
   */
  validateExpenseData(expenseData: Partial<Expense>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!expenseData.description || expenseData.description.trim() === '') {
      errors.push('Description is required');
    }

    if (!expenseData.category) {
      errors.push('Category is required');
    } else {
      const validCategories = ['Software', 'Equipment', 'Marketing', 'Office', 'Travel', 'Other'];
      if (!validCategories.includes(expenseData.category)) {
        errors.push(`Invalid category. Must be one of: ${validCategories.join(', ')}`);
      }
    }

    if (expenseData.amount === undefined || expenseData.amount === null) {
      errors.push('Amount is required');
    } else if (expenseData.amount < 0) {
      errors.push('Amount cannot be negative');
    }

    if (!expenseData.date) {
      errors.push('Date is required');
    } else {
      const expenseDate = new Date(expenseData.date);
      if (isNaN(expenseDate.getTime())) {
        errors.push('Date must be a valid date');
      }
    }

    // Validate currency code if provided
    if (expenseData.currency) {
      const validCurrencyCodes = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'INR'];
      if (!validCurrencyCodes.includes(expenseData.currency)) {
        errors.push(`Invalid currency code. Must be one of: ${validCurrencyCodes.join(', ')}`);
      }
    }

    // If expense is billable, client must be specified
    if (expenseData.isBillable && !expenseData.clientId) {
      errors.push('Client must be specified for billable expenses');
    }

    // Validate receipt URL if provided
    if (expenseData.receiptUrl) {
      try {
        new URL(expenseData.receiptUrl);
      } catch {
        errors.push('Receipt URL must be a valid URL');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Check if expense has dependencies (expenses don't have dependencies)
   */
  hasDependencies(_id: string): RepositoryResult<boolean> {
    return {
      success: true,
      data: false,
    };
  }

  /**
   * Calculate expense tax (if applicable)
   */
  calculateTax(amount: number, taxRate: number): number {
    return amount * (taxRate / 100);
  }

  /**
   * Determine if expense is within budget
   */
  isWithinBudget(amount: number, budget: number): boolean {
    return amount <= budget;
  }

  /**
   * Get budget utilization percentage
   */
  getBudgetUtilization(spent: number, budget: number): number {
    if (budget <= 0) return 100;
    return (spent / budget) * 100;
  }

  /**
   * Categorize expense by amount (small, medium, large)
   */
  categorizeByAmount(amount: number): 'small' | 'medium' | 'large' {
    if (amount < 100) return 'small';
    if (amount < 1000) return 'medium';
    return 'large';
  }

  /**
   * Check if expense requires approval (based on amount threshold)
   */
  requiresApproval(amount: number, threshold: number = 1000): boolean {
    return amount >= threshold;
  }

  /**
   * Format expense amount with currency
   */
  formatAmount(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  /**
   * Generate expense description from category and notes
   */
  generateDescription(category: string, notes?: string): string {
    if (notes && notes.trim()) {
      return `${category}: ${notes}`;
    }
    return category;
  }

  /**
   * Validate receipt file type
   */
  validateReceiptFileType(fileName: string): { valid: boolean; error?: string } {
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.pdf', '.gif', '.bmp'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));

    if (!allowedExtensions.includes(extension)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: ${allowedExtensions.join(', ')}`,
      };
    }

    return { valid: true };
  }

  /**
   * Check if expense date is within current fiscal year
   */
  isCurrentFiscalYear(date: string, fiscalYearStart: number = 1): boolean {
    const expenseDate = new Date(date);
    const now = new Date();

    // Simple check: same calendar year
    // For more complex fiscal year logic, customize as needed
    return expenseDate.getFullYear() === now.getFullYear();
  }

  /**
   * Calculate expense age in days
   */
  calculateExpenseAge(date: string): number {
    const expenseDate = new Date(date);
    const now = new Date();
    const diffInMs = now.getTime() - expenseDate.getTime();
    return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  }

  /**
   * Check if expense is recent (within last 30 days)
   */
  isRecentExpense(date: string): boolean {
    const age = this.calculateExpenseAge(date);
    return age <= 30;
  }

  /**
   * Duplicate an expense (create a new expense from existing one)
   */
  async duplicateExpense(expenseId: string): Promise<RepositoryResult<Expense>> {
    try {
      // Import dynamically to avoid circular dependency
      const { expenseQueryRepository } = await import('./ExpenseQueryRepository');

      // Get the original expense
      const result = await expenseQueryRepository.findById(expenseId);

      if (!result.success || !result.data) {
        return {
          success: false,
          error: 'Expense not found',
        };
      }

      const original = result.data;

      // Create duplicate expense with modified description
      const duplicateData: Omit<Expense, 'id' | 'createdAt'> = {
        userId: original.userId,
        category: original.category,
        description: `${original.description} (Copy)`,
        amount: original.amount,
        date: new Date().toISOString(), // Use today's date
        receiptUrl: original.receiptUrl,
        notes: original.notes,
        currency: original.currency,
        isBillable: original.isBillable,
        clientId: original.clientId,
      };

      // Import command repository
      const { expenseCommandRepository } = await import('./ExpenseCommandRepository');

      return expenseCommandRepository.create(original.userId, duplicateData);
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'duplicating expense'),
      };
    }
  }

  /**
   * Helper method to handle errors consistently
   */
  private handleError(error: any, context: string): string {
    console.error(`Error ${context}:`, error);
    return error?.message || `Failed to ${context}`;
  }
}

// Export singleton instance
export const expenseWorkflowService = new ExpenseWorkflowService();
